import React, { useState, useCallback } from 'react';
import { CustomerSearchView } from './CustomerSearchView';
import { OrderBuilderView } from './OrderBuilderView';
import { CustomerDetailDto } from '../../../../types/directOrder';

type AppState = 'search' | 'build' | 'done';

interface DirectCustomerOrderAppProps {
  onNavigateToPicking?: (pListId: number) => void;
  onNavigateBack?: () => void;
}

export const DirectCustomerOrderApp: React.FC<DirectCustomerOrderAppProps> = ({
  onNavigateToPicking,
  onNavigateBack
}) => {
  const [appState, setAppState] = useState<AppState>('search');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetailDto | null>(null);
  const [doneMessage, setDoneMessage] = useState<string>('');

  const handleCustomerSelected = useCallback((customer: CustomerDetailDto) => {
    setSelectedCustomer(customer);
    setAppState('build');
  }, []);

  const handleBackToSearch = useCallback(() => {
    setSelectedCustomer(null);
    setAppState('search');
  }, []);

  const handleOrderCompleted = useCallback((message: string) => {
    setDoneMessage(message);
    setAppState('done');
  }, []);

  const handlePListCreated = useCallback((pListId: number) => {
    if (onNavigateToPicking) {
      onNavigateToPicking(pListId);
    }
  }, [onNavigateToPicking]);

  const handleNewOrder = useCallback(() => {
    setSelectedCustomer(null);
    setDoneMessage('');
    setAppState('search');
  }, []);

  if (appState === 'done') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-green-900 mb-2">Ordre fullført</h2>
          <p className="text-green-700 mb-6">{doneMessage}</p>
          <div className="flex gap-3">
            <button
              onClick={handleNewOrder}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Ny ordre
            </button>
            {onNavigateBack && (
              <button
                onClick={onNavigateBack}
                className="flex-1 px-4 py-3 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 font-medium"
              >
                Tilbake
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (appState === 'build' && selectedCustomer) {
    return (
      <OrderBuilderView
        customer={selectedCustomer}
        onBack={handleBackToSearch}
        onOrderCompleted={handleOrderCompleted}
        onPListCreated={handlePListCreated}
      />
    );
  }

  return (
    <CustomerSearchView
      onCustomerSelected={handleCustomerSelected}
      onBack={onNavigateBack}
    />
  );
};
